import { useEffect } from 'react';

import { Box } from 'components/Box';
import { SecondaryButton } from 'components/Button';
import { CmsSocialButtons } from 'components/CmsSocialButtons';
import SubscribeToSubsiteContainer from 'containers/SubscribeToSubsiteContainer';
import { CmsApiBlock } from 'modules/cms/api/types/CmsApiBlock';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { CmsBelowBlocks } from 'modules/cms/components/BelowBlocks';
import { CmsBody } from 'modules/cms/components/Body';
import { CmsHeaderImage } from 'modules/cms/components/HeaderImage';
import { CmsLowerSidebar } from 'modules/cms/components/LowerSidebar';
import { CmsSubscribed } from 'modules/cms/components/Subscribed';
import { CmsUpperSidebar } from 'modules/cms/components/UpperSidebar';
import {
  getCmsBlockHasBackground,
  getIsContentFullWidthLayout,
} from 'modules/cms/helpers';
import { useCmsApiPageSubsite } from 'modules/cms/hooks';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackPageView } from 'utils/analytics/track/trackPageView';
import { isNotEmpty } from 'utils/functional';

type Props = {
  content: CmsApiPage;
};

export function CmsContentPageWrapper({ content }: Props) {
  const fullWidthLayout = getIsContentFullWidthLayout({ content });
  const hasBelowBlocks =
    content.hasRecommendedJobs ||
    (content.tags && content.tags.length > 0) ||
    content.author;

  const subsite = useCmsApiPageSubsite({ cmsApiPage: content });

  useEffect(() => {
    trackPageView('CMS Page', {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      subsite_name: subsite.publicName,
      page_title: content.title,
      page_type: content.type,
      categories: content.categories.map((category) => category.name),
    });
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  }, [content, subsite.publicName]);

  const firstBlock: CmsApiBlock | undefined = content.contentBlocks[0];
  const lastBlock = content.contentBlocks[content.contentBlocks.length - 1] || {
    type: undefined,
  };

  return (
    <CmsBody
      content={content}
      padTop={
        isNotEmpty(content.contentBlocks) &&
        !(
          [
            'STICKY_HEADER_LINKS',
            'STICKY_HEADER_LINKS_GRAD',
            'CTA_HERO',
            'GRAD_CTA_HERO',
            'DONATE',
            'SIGN_UP',
            'SCROLL_TRACKING',
          ].includes(firstBlock.type) ||
          (firstBlock.type === 'SPLIT_CONTENT' &&
            ['FULL_WIDTH', 'OVERLAPPING'].includes(
              firstBlock.data.appearance
            )) ||
          getCmsBlockHasBackground({ block: firstBlock, fullWidthLayout })
        )
      }
      padBottom={
        !['CTA_HERO', 'GRAD_CTA_HERO', 'SPLIT_CONTENT'].includes(lastBlock.type)
      }
      headerImage={content.headerImage && <CmsHeaderImage content={content} />}
      socialButtons={
        content.hasSocialSharing && (
          <CmsSocialButtons
            content={content}
            message={content.metaDescription}
            title={content.title}
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            subsite={subsite.publicName}
          />
        )
      }
      subscribeButton={
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        subsite.canSubscribe &&
        content.type === 'POST' && (
          <Box m="6px">
            <SubscribeToSubsiteContainer
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              subsiteId={subsite.id}
              render={({ subscribe, isSubscribed, submitting }) => (
                <SecondaryButton
                  size="small"
                  onClick={() => {
                    subscribe();
                    trackClicked('Subscribe', {
                      context: 'CMS Page',
                    });
                  }}
                  data-qa-id="subscribe-to-subsite"
                  disabled={isSubscribed}
                >
                  <CmsSubscribed
                    submitting={submitting}
                    isSubscribed={isSubscribed}
                  />
                </SecondaryButton>
              )}
            />
          </Box>
        )
      }
      belowBlocks={hasBelowBlocks ? <CmsBelowBlocks content={content} /> : null}
      upperSidebar={
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        content.sidebar && content.sidebar.contentBlocksTop.length ? (
          <CmsUpperSidebar content={content} />
        ) : null
      }
      lowerSidebar={
        content.sidebar && content.sidebar.contentBlocksBottom.length > 0 ? (
          <CmsLowerSidebar content={content} />
        ) : null
      }
    />
  );
}
